"use client"

import { motion } from "framer-motion"
import { Heart, Activity, Moon, Droplet, Wind, Smile, TrendingUp, Target } from "lucide-react"

export default function WellnessView() {
  const wellnessTips = [
    {
      id: 1,
      title: "Get Quality Sleep",
      description: "Aim for 7-9 hours of sleep to allow your brain to consolidate memories and clear toxins",
      icon: Moon,
      color: "#667eea",
      benefits: ["Memory consolidation", "Toxin removal", "Mood regulation"]
    },
    {
      id: 2,
      title: "Stay Hydrated",
      description: "Your brain is 75% water. Even mild dehydration can affect cognitive function",
      icon: Droplet,
      color: "#4cc9f0",
      benefits: ["Better focus", "Improved memory", "Enhanced mood"]
    },
    {
      id: 3,
      title: "Regular Exercise",
      description: "Physical activity increases blood flow to the brain and promotes neuroplasticity",
      icon: Activity,
      color: "#06ffa5",
      benefits: ["Neurogenesis", "Stress reduction", "Better sleep"]
    },
    {
      id: 4,
      title: "Practice Mindfulness",
      description: "Meditation and mindfulness can physically change brain structure and improve focus",
      icon: Wind,
      color: "#7209b7",
      benefits: ["Reduced anxiety", "Better attention", "Emotional control"]
    },
    {
      id: 5,
      title: "Social Connection",
      description: "Social interactions stimulate multiple brain regions and improve cognitive health",
      icon: Smile,
      color: "#ff006e",
      benefits: ["Mental stimulation", "Stress relief", "Happiness boost"]
    },
    {
      id: 6,
      title: "Continuous Learning",
      description: "Learning new skills creates new neural pathways and keeps your brain young",
      icon: Target,
      color: "#ffd60a",
      benefits: ["Neuroplasticity", "Memory improvement", "Cognitive reserve"]
    }
  ]

  const brainHealthMetrics = [
    { label: "Cognitive Function", value: 85, color: "#667eea" },
    { label: "Emotional Balance", value: 78, color: "#ff006e" },
    { label: "Sleep Quality", value: 72, color: "#4cc9f0" },
    { label: "Stress Management", value: 68, color: "#ffd60a" }
  ]

  return (
    <div className="wellness-view">
      <motion.div
        className="wellness-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="wellness-title">
          <Heart className="title-icon" />
          Brain Wellness Center
        </h1>
        <p className="wellness-subtitle">
          Optimize your brain health with science-backed practices
        </p>
      </motion.div>

      {/* Health Metrics */}
      <motion.div
        className="health-metrics glass-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h2 className="section-title">
          <TrendingUp size={24} />
          Your Brain Health Score
        </h2>
        <div className="metrics-grid">
          {brainHealthMetrics.map((metric, index) => (
            <motion.div
              key={index}
              className="metric-item"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 + index * 0.1 }}
            >
              <div className="metric-header">
                <span className="metric-label">{metric.label}</span>
                <span className="metric-value">{metric.value}%</span>
              </div>
              <div className="metric-bar">
                <motion.div
                  className="metric-fill"
                  style={{ backgroundColor: metric.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${metric.value}%` }}
                  transition={{ duration: 1, delay: 0.3 + index * 0.1 }}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Wellness Tips Grid */}
      <div className="wellness-tips-grid">
        {wellnessTips.map((tip, index) => {
          const Icon = tip.icon
          return (
            <motion.div
              key={tip.id}
              className="wellness-tip-card glass-card"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="tip-icon" style={{ backgroundColor: `${tip.color}20`, color: tip.color }}>
                <Icon size={32} />
              </div>
              
              <div className="tip-content">
                <h3 className="tip-title">{tip.title}</h3>
                <p className="tip-description">{tip.description}</p>
                
                <div className="tip-benefits">
                  <h4>Benefits:</h4>
                  <ul>
                    {tip.benefits.map((benefit, idx) => (
                      <li key={idx}>
                        <span className="benefit-dot" style={{ backgroundColor: tip.color }} />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Daily Routine Suggestion */}
      <motion.div
        className="daily-routine glass-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
      >
        <h2 className="section-title">
          <Activity size={24} />
          Recommended Daily Brain Care Routine
        </h2>
        <div className="routine-timeline">
          {[
            { time: "Morning", activity: "Hydrate + Light Exercise", duration: "30 min" },
            { time: "Mid-Morning", activity: "Deep Work Session", duration: "90 min" },
            { time: "Afternoon", activity: "Learning + Social Time", duration: "60 min" },
            { time: "Evening", activity: "Mindfulness Practice", duration: "20 min" },
            { time: "Night", activity: "Quality Sleep", duration: "7-9 hrs" }
          ].map((item, index) => (
            <motion.div
              key={index}
              className="routine-item"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 + index * 0.1 }}
            >
              <div className="routine-time">{item.time}</div>
              <div className="routine-details">
                <h4>{item.activity}</h4>
                <span className="routine-duration">{item.duration}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  )
}
